// npm i rbush
// If you don't have types:  npm i -D @types/rbush
// JointJS: @joint/core

import RBush from "rbush";
import { dia, g } from "@joint/core";
import RappidContext from "../../rappid/rappid-context";
import { Transition, TransitionView } from "../../mvc/transition";

// ---- Tunables (quick knobs) ----
const EDGE_PADDING = 1; // treat edges as this-thick when detecting overlap
const NODE_PADDING = 2; // extra padding around nodes when avoiding

type IndexedBox = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  id: string;
};

type Candidate = {
  dist: number;
  xOff: number;
  yOff: number;
  htmlRect: g.Rect;
  jointjsRect: g.Rect;
  cost?: number;
};

export class EdgeLabelPlacer {
  private graph: dia.Graph;
  private context: RappidContext;
  private index: RBush<IndexedBox>;
  private active = false;

  constructor(context: RappidContext) {
    this.graph = context.graph;
    this.context = context;
    this.index = new RBush<IndexedBox>();
  }

  public setActive(value: boolean): void {
    this.active = value;
    if (value === false) {
      this.index.clear();
    }
  }

  public placeAllLabels(): void {
    if (!this.active) return;
    for (const link of this.graph.getLinks()) {
      // NOTE: I had this line before
      // link.findView(this.context.paper).render();
      // I removed it because Debugger revealed: The link route at this point is not final
      // Hence, the LabelPlacer picked wrong label positions.
      // It seems that JointJS redraws the link even after render().
      this.placeLabel(link);
    }
  }

  /** Call this after each successful route */
  public placeLabel(link: dia.Link): void {
    if (!this.active) {
      return;
    }
    const id = link.id.toString();

    const linkView = link.findView(this.context.paper) as TransitionView;
    const prevBox = linkView.getLabelBBox();
    if (!prevBox) {
      return;
    }
    const labelsArr = link.get("labels") as dia.Link.Label[] | undefined;
    const labelIdx = (link as Transition).getLabelIndex();
    const label: dia.Link.Label =
      Array.isArray(labelsArr) && labelsArr.length > labelIdx ? labelsArr[labelIdx] : (null as dia.Link.Label);
    if (!label || !label.attrs?.text.text) {
      return;
    }

    this.removeFromIndex(new g.Rect(prevBox.x, prevBox.y, prevBox.width, prevBox.height), id);
    const candidates = this.calcCandidates(link, linkView, prevBox);
    const best = this.pickBest(candidates);
    // Update the label object in the array
    labelsArr[labelIdx] = {
      ...label,
      position: { distance: best.dist, offset: { x: best.xOff, y: best.yOff } },
    };
    // Update the labels array and trigger a re-render
    link.set("labels", labelsArr, { avoidRouter: true });
    this.insertToIndex(best.htmlRect, id);
  }

  private calcCandidates(link: dia.Link, linkView: TransitionView, prevBBox: DOMRect): Candidate[] {
    const Coords: Array<[number, number]> = [
      [0.5, 1],
      [0.5, -1],
      [0.15, 1],
      [0.15, -1],
      [0.85, 1],
      [0.85, -1],
    ];
    const cands: Candidate[] = [];
    const polyline = this.edgePolyline(link);
    for (const [dist, f] of Coords) {
      const segment = this.segmentAtDistance(polyline, dist);
      const dx = segment.b.x - segment.a.x;
      const dy = segment.b.y - segment.a.y;
      let direction: "left" | "right" | "up" | "down";
      if (Math.abs(dx) > Math.abs(dy)) {
        direction = dx > 0 ? "right" : "left";
      } else {
        direction = dy > 0 ? "down" : "up";
      }
      const isVertical = direction === "up" || direction === "down";
      const xCorrection = prevBBox.width / 2 + 5;
      const yCorrection = prevBBox.height / 2 + 5;
      let yOffset = isVertical ? 0 : f * yCorrection;
      let xOffset = isVertical ? f * xCorrection + 2 : 0;
      if (dist < 0.5 && segment.a === polyline[0]) {
        // label likely to overlap with start node
        switch (direction) {
          case "left":
            xOffset -= xCorrection;
            break;
          case "right":
            xOffset += xCorrection;
            break;
          default:
            break;
        }
        const sourceAnchor = link.get("source")?.anchor?.args;
        // the avoid router places parallel edges using anchor.dx/dy.
        // We take this over to place labels accordingly
        if (sourceAnchor) {
          if (isVertical && sourceAnchor.dx < 100) {
            xOffset += sourceAnchor.dx;
          } else if (sourceAnchor.dy < 100) {
            yOffset += sourceAnchor.dy;
          }
        }
      }

      if (dist > 0.5 && segment.b === polyline[polyline.length - 1]) {
        // label likely to overlap with end node
        switch (direction) {
          case "left":
            xOffset += xCorrection;
            break;
          case "right":
            xOffset -= xCorrection;
            break;
          default:
            break;
        }
        // the avoid router places parallel edges using anchor.dx/dy.
        // We take this over to place labels accordingly
        const targetAnchor = link.get("target")?.anchor?.args;
        if (targetAnchor) {
          if (isVertical && targetAnchor.dx < 100) {
            xOffset += targetAnchor.dx ?? 0;
          } else if (targetAnchor.dy < 100) {
            yOffset += targetAnchor.dy ?? 0;
          }
        }
      }

      const coords = linkView.getLabelCoordinates({
        distance: dist,
        offset: { x: xOffset, y: yOffset },
      });
      const cand = {
        dist,
        xOff: xOffset,
        yOff: yOffset,
        htmlRect: new g.Rect(coords.x - xCorrection, coords.y - yCorrection, prevBBox.width, prevBBox.height),
        jointjsRect: new g.Rect(coords.x, coords.y, prevBBox.width, prevBBox.height),
      } as Candidate;
      cand.cost = this.cost(cand, link);
      if (dist === 0.5) {
        cand.cost -= 5;
      }
      cands.push(cand);
    }
    return cands;
  }

  private cost(c: Candidate, link: dia.Link): number {
    const bbox = c.htmlRect;
    let cost = this.hitsNodesMalus(bbox, link);
    cost += this.hitsEdgeMalus(bbox);
    cost += this.labelOverlapPenalty(bbox);
    return cost;
  }

  private labelOverlapPenalty(bbox: g.Rect): number {
    const hits = this.index.search(this.toRBushBox(bbox));
    let penalty = 0;
    for (const h of hits) {
      const inter = bbox.intersect(new g.Rect(h.minX, h.minY, h.maxX - h.minX, h.maxY - h.minY));
      if (inter) {
        penalty += inter.width * inter.height;
      }
    }
    return penalty * 0.1;
  }

  private pickBest(cands: Candidate[]): Candidate | null {
    const scored = cands.filter((c) => Number.isFinite(c.cost)).sort((a, b) => a.cost! - b.cost!);
    return scored[0] ?? null;
  }

  // ---- Geometry ----

  /**
   * Returns the segment (as {a: g.Point, b: g.Point, i: number}) of the polyline at the given normalized distance (0..1).
   * The distance is interpreted as a percentage along the total length of the polyline.
   */
  private segmentAtDistance(poly: g.Point[], distance: number): { a: g.Point; b: g.Point; i: number } | null {
    if (poly.length < 2) return null;
    // Clamp distance to [0, 1]
    const pct = Math.max(0, Math.min(1, distance));
    // Compute total length
    let totalLen = 0;
    const segLens: number[] = [];
    for (let i = 0; i < poly.length - 1; i++) {
      const len = poly[i].distance(poly[i + 1]);
      segLens.push(len);
      totalLen += len;
    }
    const targetLen = pct * totalLen;
    // Find the segment containing the targetLen
    let acc = 0;
    for (let i = 0; i < segLens.length; i++) {
      if (acc + segLens[i] >= targetLen) {
        return { a: poly[i], b: poly[i + 1], i };
      }
      acc += segLens[i];
    }
    // If not found, return the last segment
    const lastIdx = poly.length - 2;
    return lastIdx >= 0 ? { a: poly[lastIdx], b: poly[lastIdx + 1], i: lastIdx } : null;
  }

  private edgePolyline(link: dia.Link): g.Point[] {
    const srcEl = link.getSourceElement?.() as dia.Element | null;
    const trgEl = link.getTargetElement?.() as dia.Element | null;
    if (!srcEl || !trgEl) return [];

    const srcC = srcEl.getBBox().center();
    const trgC = trgEl.getBBox().center();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const source = link.get("source") as any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const target = link.get("target") as any;

    const sdx = source?.anchor?.args?.dx ?? 0;
    const sdy = source?.anchor?.args?.dy ?? 0;
    const tdx = target?.anchor?.args?.dx ?? 0;
    const tdy = target?.anchor?.args?.dy ?? 0;

    const p0 = new g.Point(srcC.x + sdx, srcC.y + sdy);
    const pN = new g.Point(trgC.x + tdx, trgC.y + tdy);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const verts = (link.get("vertices") || []).map((v: any) => new g.Point(v));
    return [p0, ...verts, pN];
  }

  private graphNodeRects(): Array<{ rect: g.Rect; id: string }> {
    return this.graph.getElements().map((el) => {
      const r = el.getBBox().inflate(NODE_PADDING);
      return { rect: r, id: el.id.toString() };
    });
  }

  private hitsNodesMalus(bbox: g.Rect, link: dia.Link): number {
    const linkSrc = (link.getSourceElement?.() as dia.Element | null)?.id?.toString();
    const linkTrg = (link.getTargetElement?.() as dia.Element | null)?.id?.toString();
    let result = 0;
    for (const { rect, id } of this.graphNodeRects()) {
      if (rect.intersect(bbox)) {
        if (id === linkSrc || id === linkTrg) {
          result += 50;
        } else {
          result += 100;
        }
      }
    }
    return result;
  }

  private edgeRects(): g.Rect[] {
    const out: g.Rect[] = [];
    for (const l of this.graph.getLinks()) {
      const poly = this.edgePolyline(l);
      for (let i = 0; i < poly.length - 1; i++) {
        const segBox = new g.Line(poly[i], poly[i + 1]).bbox().inflate(EDGE_PADDING);
        out.push(segBox);
      }
    }
    return out;
  }

  private hitsEdgeMalus(bbox: g.Rect): number {
    let result = 0;
    for (const r of this.edgeRects()) {
      if (r.intersect(bbox)) {
        result += 30;
      }
    }
    return result;
  }

  private insertToIndex(bbox: g.Rect, id: string): void {
    this.index.insert({ id, ...this.toRBushBox(bbox) });
  }

  private removeFromIndex(bbox: g.Rect, id: string): void {
    this.index.remove({ id, ...this.toRBushBox(bbox) }, (a, b) => a.id === b.id);
  }

  private toRBushBox(b: g.Rect): Omit<IndexedBox, "id"> {
    return { minX: b.x, minY: b.y, maxX: b.x + b.width, maxY: b.y + b.height };
  }
}
